import React, { useState, useEffect, useCallback } from 'react';
import styled, { useTheme, css } from 'styled-components';
import { cardBackground, cardBorder, cardShadow } from '../styles/themeHelpers';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFieldAuth } from '../contexts/FieldContext';
import userService from '../services/userService';
import ranksService, { Rank } from '../services/ranksService';
import { Player } from '../types/user';
import ModernBackground from '../components/common/ModernBackground';

const withAlpha = (hexColor: string, alpha: number) => {
  const hex = hexColor.replace('#', '');
  const normalized = hex.length === 3
    ? hex.split('').map(char => `${char}${char}`).join('')
    : hex;
  const bigint = parseInt(normalized, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const isShadowTheme = (theme: any) => theme.name === 'shadowGrid';
const isModernTheme = (theme: any) => theme.name === 'modernWarfare';
const isNightOps = (theme: any) => theme.name === 'nightOps';
const shadowGridChrome = css`
  ${({ theme }) => theme.name === 'shadowGrid' && css`
    position: relative;
    border: 1px solid rgba(0, 212, 255, 0.35);
    background: ${cardBackground(theme)};
    box-shadow: 0 20px 45px rgba(0, 0, 0, 0.6), 0 0 28px rgba(0, 212, 255, 0.2);
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      border: 1px solid rgba(0, 212, 255, 0.2);
      opacity: 0.25;
      pointer-events: none;
    }
  `}
`;

const PageBackground = styled.div<{ $shadow?: boolean }>`
  min-height: 100vh;
  background: ${({ theme }) =>
    theme.profile.pageBackground === 'transparent'
      ? 'transparent'
      : theme.gradients.background || theme.colors.background};
  padding: 20px;
  padding-top: 100px;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding-top: 90px;
    padding: 10px;
    padding-top: 90px;
  }
`;

const DottedOverlay = styled.div`
  display: none;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  color: ${({ theme }) => theme.colors.text};
  position: relative;
  z-index: 3;
  font-family: ${({ theme }) => {
    if (theme.name === 'shadowGrid') return "'Courier New', monospace";
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return 'inherit';
  }};
  
  @media (max-width: 1240px) {
    max-width: 95%;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    margin-bottom: 30px;
  }
`;

const GameTypeSelector = styled.div`
  display: flex;
  justify-content: center;
  gap: 2px;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    margin-bottom: 25px;
  }
`;

const GameTypeButton = styled.button<{ $active: boolean }>`
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Chakra Petch', sans-serif" : "'Courier New', monospace"};
  font-size: 14px;
  padding: 12px 24px;
  background: ${props => props.$active ? props.theme.colors.primary : withAlpha(props.theme.colors.primary, 0.1)};
  color: ${props => props.$active ? (isShadowTheme(props.theme) ? '#000000' : '#00121f') : props.theme.colors.primary};
  border: 2px solid ${props => props.theme.colors.primary};
  cursor: pointer;
  text-transform: uppercase;
  font-weight: bold;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.$active ? props.theme.colors.primary : withAlpha(props.theme.colors.primary, 0.2)};
    box-shadow: 0 0 15px ${props => withAlpha(props.theme.colors.primary, 0.4)};
  }

  &:first-child {
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
    border-right: 1px solid ${props => props.theme.colors.primary};
  }

  &:last-child {
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
    border-left: 1px solid ${props => props.theme.colors.primary};
  }

  @media (max-width: 768px) {
    padding: 15px 20px;
    font-size: 16px;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0 0 10px 0;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-shadow: 0 0 15px ${({ theme }) => withAlpha(theme.colors.primary, 0.7)};
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Rajdhani', sans-serif" : "inherit"};

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 15px;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.secondary};
  margin: 0;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.4;
    padding: 0 10px;
  }
`;

const SearchSection = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  align-items: center;
  background: ${({ theme }) => cardBackground(theme)};
  border: 1px solid ${({ theme }) => cardBorder(theme)};
  border-radius: 12px;
  padding: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
    margin-bottom: 25px;
    padding: 15px;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  background: ${({ theme }) => withAlpha(theme.colors.primary, 0.1)};
  border: 1px solid ${({ theme }) => withAlpha(theme.colors.primary, 0.3)};
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;

  &::placeholder {
    color: ${({ theme }) => theme.colors.secondary};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 8px ${({ theme }) => withAlpha(theme.colors.primary, 0.5)};
    background: ${({ theme }) => withAlpha(theme.colors.primary, 0.15)};
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 15px 20px;
    font-size: 16px;
    border-radius: 10px;
  }
`;

const ActionButton = styled.button`
  background: ${({ theme }) => withAlpha(theme.colors.primary, 0.1)};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primary};
  padding: 12px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Chakra Petch', sans-serif" : "inherit"};
  text-transform: uppercase;
  letter-spacing: 1px;

  &:hover {
    background: ${({ theme }) => withAlpha(theme.colors.primary, 0.2)};
    transform: translateY(-2px);
    box-shadow: 0 4px 15px ${({ theme }) => withAlpha(theme.colors.primary, 0.3)};
    text-shadow: 0 0 8px ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 15px 30px;
    font-size: 16px;
    border-radius: 10px;
  }
`;

const SearchingIndicator = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.primary};
  margin: 20px 0;
  font-style: italic;
`;

const LoadingSpinner = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.2rem;
  margin: 40px 0;
`;

const ErrorMessage = styled.div`
  background: ${({ theme }) => withAlpha(theme.colors.error, 0.1)};
  border: 1px solid ${({ theme }) => theme.colors.error};
  color: ${({ theme }) => theme.colors.error};
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  margin: 20px 0;
`;

const RetryButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  border: none;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s ease;
  margin-top: 15px;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-2px);
  }
`;

const TopPlayersSection = styled.div`
  margin-bottom: 50px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0 0 20px 0;
  text-transform: uppercase;
  letter-spacing: 1px;
  text-align: center;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Rajdhani', sans-serif" : "inherit"};

  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 15px;
  }
`;

const TopPlayersList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const TopPlayerRank = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  background: ${({ theme }) => withAlpha(theme.colors.primary, 0.2)};
  color: ${({ theme }) => theme.colors.primary};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: bold;
  opacity: 0;
  transition: opacity 0.3s ease;

  @media (max-width: 768px) {
    position: static;
    align-self: flex-end;
    margin-bottom: 10px;
    opacity: 1; /* На мобильных всегда показываем */
  }
`;

const TopPlayerItem = styled.div`
  background: ${({ theme }) => cardBackground(theme)};
  border: 1px solid ${({ theme }) => cardBorder(theme)};
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 200px;
  ${shadowGridChrome};

  &:hover {
    background: ${({ theme }) => cardBackground(theme)};
    border-color: ${({ theme }) => cardBorder(theme)};
    transform: translateX(5px);
    box-shadow: ${({ theme }) => cardShadow(theme)};
    
    ${TopPlayerRank} {
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    padding: 25px;
    height: auto;
  }
`;

const TopPlayerInfo = styled.div`
  margin-bottom: 15px;
  min-height: 70px;

  @media (max-width: 768px) {
    margin-bottom: 20px;
    min-height: auto;
  }
`;

const TopPlayerHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

const TopPlayerNameColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
`;

const TopPlayerRankTitle = styled.div<{ $faction?: 'human' | 'alien' }>`
  color: ${props => 
    props.$faction === 'human' ? props.theme.colors.primary : 
    props.$faction === 'alien' ? props.theme.colors.warning : 
    props.theme.colors.text
  };
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Chakra Petch', sans-serif" : "'Courier New', monospace"};

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const TopPlayerName = styled.h3`
  font-size: 1.4rem;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0 0 8px 0;
  line-height: 1.3;
  word-wrap: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-height: 56px;
  transform: translateY(1px);
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Rajdhani', sans-serif" : "inherit"};

  @media (max-width: 768px) {
    font-size: 1.6rem;
    margin-bottom: 10px;
    max-height: none;
    -webkit-line-clamp: none;
    transform: none;
  }
`;


const TopPlayerStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
  margin: 15px 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
    margin: 20px 0;
  }
`;

const TopPlayerStat = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;

  @media (max-width: 768px) {
    padding: 12px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const TopPlayerStatLabel = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.9rem;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const TopPlayerStatValue = styled.span<{ $isHumanBetter?: boolean; $isPoints?: boolean }>`
  color: ${props => 
    props.$isPoints ? 
      (props.$isHumanBetter ? props.theme.colors.primary : props.theme.colors.warning) : 
      props.theme.colors.text
  };
  font-weight: bold;
  font-size: 1rem;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const TopPlayerFooter = styled.div`
  margin-top: auto;
`;

const TopPlayerRankBadge = styled.span`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => isShadowTheme(theme) ? 'white' : '#00121f'};
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: bold;
  text-align: center;
  word-wrap: break-word;
  display: inline-block;

  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 1rem;
    border-radius: 8px;
  }
`;

const AllPlayersSection = styled.div`
  margin-bottom: 40px;
`;

const PlayersList = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 15px;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const PlayerListItem = styled.div`
  background: ${({ theme }) => cardBackground(theme)};
  border: 1px solid ${({ theme }) => cardBorder(theme)};
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: grid;
  grid-template-columns: auto 250px auto auto auto auto;
  gap: 20px;
  align-items: center;
  ${shadowGridChrome};

  &:hover {
    background: ${({ theme }) => cardBackground(theme)};
    border-color: ${({ theme }) => cardBorder(theme)};
    transform: translateX(5px);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
    padding: 25px;
  }
`;

const PlayerRankPosition = styled.div`
  background: ${({ theme }) => withAlpha(theme.colors.primary, 0.2)};
  color: ${({ theme }) => theme.colors.primary};
  padding: 8px 12px;
  border-radius: 6px;
  font-weight: bold;
  font-size: 0.9rem;
  text-align: center;
  min-width: 50px;

  @media (max-width: 768px) {
    align-self: center;
    margin-bottom: 10px;
  }
`;

const PlayerMainInfo = styled.div`
  min-width: 0;
`;

const PlayerListHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

const PlayerListName = styled.h3`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Rajdhani', sans-serif" : "inherit"};

  @media (max-width: 768px) {
    font-size: 1.4rem;
    white-space: normal;
  }
`;

const PlayerListBase = styled.span`
  background: ${({ theme }) => withAlpha(theme.colors.primary, 0.2)};
  color: ${({ theme }) => theme.colors.primary};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: bold;

  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 0.9rem;
  }
`;

const PlayerListLocation = styled.p`
  color: ${({ theme }) => theme.colors.textMuted};
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.4;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const PlayerStatsGrid = styled.div`
  display: grid;
  grid-template-columns: 120px 120px 120px;
  gap: 15px;
  min-width: 400px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
    min-width: auto;
  }
`;

const PlayerStatItem = styled.div`
  text-align: center;
  padding: 8px;
  background: ${({ theme }) => withAlpha(theme.colors.surface, 0.5)};
  border-radius: 6px;
  min-width: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const PlayerStatItemLabel = styled.span`
  display: block;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.8rem;
  margin-bottom: 4px;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 8px;
  }
`;

const PlayerStatItemValue = styled.span<{ $isHumanBetter?: boolean; $isPoints?: boolean }>`
  display: block;
  color: ${props => 
    props.$isPoints ? 
      (props.$isHumanBetter ? props.theme.colors.primary : props.theme.colors.warning) : 
      props.theme.colors.text
  };
  font-weight: bold;
  font-size: 1rem;
  min-width: 40px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;



const PlayerRanksInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 150px;

  @media (max-width: 768px) {
    flex-direction: row;
    gap: 15px;
    min-width: auto;
    justify-content: space-around;
  }
`;

const PlayerRankInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 4px;
    text-align: center;
  }
`;

const PlayerRankLabel = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.8rem;
  font-weight: bold;
  font-family: 'Courier New', monospace;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const PlayerRankName = styled.div<{ $faction?: 'human' | 'alien' }>`
  color: ${props => 
    props.$faction === 'human' ? props.theme.colors.primary : 
    props.$faction === 'alien' ? props.theme.colors.warning : 
    props.theme.colors.text
  };
  font-size: 0.9rem;
  white-space: nowrap;
  font-family: 'Courier New', monospace;

  @media (max-width: 768px) {
    font-size: 1rem;
    white-space: normal;
  }
`;

// Стили для результатов поиска
const SearchResultsSection = styled.div`
  margin-bottom: 40px;
`;

const SearchResultsTitle = styled.h2`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 20px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 15px;
  }
`;

const SearchResultsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 15px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const SearchResultsCard = styled.div`
  background: ${({ theme }) => cardBackground(theme)};
  border: 1px solid ${({ theme }) => cardBorder(theme)};
  border-radius: 8px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  ${shadowGridChrome};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const SearchResultsInfo = styled.div`
  margin-bottom: 15px;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const SearchResultsName = styled.h3`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0 0 8px 0;
  word-wrap: break-word;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Rajdhani', sans-serif" : "inherit"};

  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 10px;
  }
`;

const SearchResultsTag = styled.span`
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: bold;

  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 0.9rem;
  }
`;

const SearchResultsFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const SearchResultsStats = styled.div`
  display: flex;
  gap: 15px;

  @media (max-width: 768px) {
    justify-content: space-between;
    width: 100%;
  }
`;

const SearchResultsStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media (max-width: 768px) {
    align-items: center;
  }
`;

const SearchResultsStatLabel = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.8rem;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const SearchResultsStatValue = styled.span`
  color: ${({ theme }) => theme.colors.text};
  font-weight: bold;
  font-size: 0.9rem;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const SearchResultsRankBadge = styled.span`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => isShadowTheme(theme) ? 'white' : '#00121f'};
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: bold;
  text-align: center;

  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 0.9rem;
  }
`;

const CommandosStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const CommandosStat = styled.div`
  background: ${({ theme }) => cardBackground(theme)};
  border: 1px solid ${({ theme }) => cardBorder(theme)};
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  ${shadowGridChrome};
  
  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateX(5px);
    box-shadow: 0 10px 30px ${({ theme }) => withAlpha(theme.colors.primary, 0.2)};
  }
`;

const CommandosStatLabel = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.9rem;
  margin-bottom: 5px;
`;

const CommandosStatValue = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.5rem;
  font-weight: bold;
`;

// Компонент отображения ранга
const RankDisplay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100px;
  width: 100%;
  background: ${({ theme }) => withAlpha(theme.colors.background, 0.3)};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => withAlpha(theme.colors.primary, 0.2)};
  padding: 8px;
`;

const RankImage = styled.img`
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 4px;
  margin-bottom: 5px;
`;

const RankName = styled.div<{ $faction?: 'human' | 'alien' }>`
  color: ${props => 
    props.$faction === 'human' ? props.theme.colors.primary : 
    props.$faction === 'alien' ? props.theme.colors.warning : 
    props.theme.colors.primary
  };
  font-size: 10px;
  text-align: center;
  line-height: 1.2;
  font-weight: bold;
  text-transform: uppercase;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Chakra Petch', sans-serif" : "'Courier New', monospace"};
`;

const NoRankDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  width: 100%;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #666;
  font-size: 12px;
  text-align: center;
`;

const CompactRankDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 130px;
  height: 95px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(0, 212, 255, 0.3);
  flex-shrink: 0;
  margin: 0 auto;
`;

const CompactRankImage = styled.img`
  width: 120px;
  height: 85px;
  object-fit: cover;
  border-radius: 4px;
`;

const CompactRankPlaceholder = styled.div`
  color: #666;
  font-size: 12px;
  font-weight: bold;
`;

// Компонент ранга игрока
interface PlayerRankProps {
  player: Player;
  getBestRank: (player: Player) => Rank | null;
  getPlayerStats: (player: Player) => any;
}

const PlayerRank: React.FC<PlayerRankProps> = ({ player, getBestRank, getPlayerStats }) => {
  const bestRank = getBestRank(player);
  
  if (!bestRank) {
    return <NoRankDisplay>No Rank</NoRankDisplay>;
  }
  
  // Определяем фракцию на основе лучшего ранга
  const stats = getPlayerStats(player);
  const faction = stats.isHumanBetter ? 'human' : 'alien';
  
  return (
    <RankDisplay>
      {bestRank.iconUrl ? (
        <RankImage src={bestRank.iconUrl} alt={bestRank.name} />
      ) : (
        <div style={{ width: '50px', height: '50px', background: '#333', borderRadius: '4px', marginBottom: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666', fontSize: '8px' }}>
          No Image
        </div>
      )}
      <RankName $faction={faction}>{bestRank.name}</RankName>
    </RankDisplay>
  );
};

// Компактный компонент ранга для топ-10
const CompactPlayerRank: React.FC<PlayerRankProps> = ({ player, getBestRank, getPlayerStats }) => {
  const bestRank = getBestRank(player);
  
  if (!bestRank) {
    return null;
  }
  
  return (
    <CompactRankDisplay>
      {bestRank.iconUrl ? (
        <CompactRankImage src={bestRank.iconUrl} alt={bestRank.name} />
      ) : (
        <CompactRankPlaceholder>?</CompactRankPlaceholder>
      )}
    </CompactRankDisplay>
  );
};

const CommandosPage: React.FC = () => {
  const theme = useTheme();
  const isShadowGrid = theme.name === 'shadowGrid';
  const isModernWarfare = theme.name === 'modernWarfare';
  const isNightOpsMode = theme.name === 'nightOps';

  const [topPlayers, setTopPlayers] = useState<Player[]>([]);
  const [allPlayers, setAllPlayers] = useState<Player[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMorePlayers, setHasMorePlayers] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  
  // Состояние для рангов
  const [humanRanks, setHumanRanks] = useState<Rank[]>([]);
  const [alienRanks, setAlienRanks] = useState<Rank[]>([]);
  const [ranksLoaded, setRanksLoaded] = useState(false);
  
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isFieldAuthenticated } = useFieldAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Получаем тип игры из URL параметров
  const selectedGameType = searchParams.get('type') || 'airsoft';

  // Функция для переключения типа игры
  const handleGameTypeChange = (gameType: string) => {
    setSearchParams({ type: gameType });
    // Сброс состояния при смене типа
    setSearchTerm('');
    setSearchResults([]);
    // Данные перезагрузятся автоматически через useEffect
  };
  
  // Ref для таймера поиска
  const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  // Ref для предотвращения дублирования API вызовов
  const loadingRef = React.useRef<boolean>(false);
  const currentGameTypeRef = React.useRef<string>('');

  // Определяем заголовок страницы на основе выбранного типа
  const getPageTitle = () => {
    if (selectedGameType === 'paintball') {
      return 'NovaWarfare Paintball Commandos';
    }
    return 'NovaWarfare Airsoft Commandos';
  };

  const getPageSubtitle = () => {
    if (selectedGameType === 'paintball') {
      return 'Elite tactical paintball operators leading the battlefield';
    }
    return 'Elite tactical airsoft operators leading the battlefield';
  };

  // Получить статистику игрока в зависимости от типа игры
  const getPlayerStats = useCallback((player: Player) => {
    if (selectedGameType === 'paintball') {
      const humanPoints = player.ranks?.paintballHumanRank?.points || 0;
      const alienPoints = player.ranks?.paintballAlienRank?.points || 0;
      const isHumanBetter = humanPoints >= alienPoints;
      
      return {
        humanRank: humanPoints,
        humanRankName: player.ranks?.paintballHumanRank?.rankName || 'Recruit',
        alienRank: alienPoints,
        alienRankName: player.ranks?.paintballAlienRank?.rankName || 'Disciple',
        missions: player.gameStatistics?.totalPaintballGames || 0,
        wins: player.gameStatistics?.paintballStatistics?.totalWins || 0,
        winRate: player.gameStatistics?.paintballStatistics?.winRate || 0,
        totalPoints: Math.max(humanPoints, alienPoints),
        totalRank: Math.max(humanPoints, alienPoints),
        isHumanBetter: isHumanBetter
      };
    }
    
    if (selectedGameType === 'speedball') {
      const humanPoints = player.ranks?.speedballHumanRank?.points || 0;
      const alienPoints = player.ranks?.speedballAlienRank?.points || 0;
      const isHumanBetter = humanPoints >= alienPoints;
      
      return {
        humanRank: humanPoints,
        humanRankName: player.ranks?.speedballHumanRank?.rankName || 'Recruit',
        alienRank: alienPoints,
        alienRankName: player.ranks?.speedballAlienRank?.rankName || 'Disciple',
        missions: player.gameStatistics?.totalSpeedballGames || 0,
        wins: player.gameStatistics?.speedballStatistics?.totalWins || 0,
        winRate: player.gameStatistics?.speedballStatistics?.winRate || 0,
        totalPoints: Math.max(humanPoints, alienPoints),
        totalRank: Math.max(humanPoints, alienPoints),
        isHumanBetter: isHumanBetter
      };
    }
    
    // Airsoft (default)
    const humanPoints = player.ranks?.airsoftHumanRank?.points || 0;
    const alienPoints = player.ranks?.airsoftAlienRank?.points || 0;
    const isHumanBetter = humanPoints >= alienPoints;
    
    return {
      humanRank: humanPoints,
      humanRankName: player.ranks?.airsoftHumanRank?.rankName || 'Recruit',
      alienRank: alienPoints,
      alienRankName: player.ranks?.airsoftAlienRank?.rankName || 'Disciple',
      missions: player.gameStatistics?.totalAirsoftGames || 0,
      wins: player.gameStatistics?.airsoftStatistics?.totalWins || 0,
      winRate: player.gameStatistics?.airsoftStatistics?.winRate || 0,
      totalPoints: Math.max(humanPoints, alienPoints),
      totalRank: Math.max(humanPoints, alienPoints),
      isHumanBetter: isHumanBetter
    };
  }, [selectedGameType]);

  // Загрузка рангов
  const loadRanks = useCallback(async () => {
    if (ranksLoaded) return;
    
    try {
      const [humanRanksData, alienRanksData] = await Promise.all([
        ranksService.getRanksByFaction('human'),
        ranksService.getRanksByFaction('alien')
      ]);
      
      setHumanRanks(humanRanksData);
      setAlienRanks(alienRanksData);
      setRanksLoaded(true);
    } catch (error) {
      console.error('Error loading ranks:', error);
      setHumanRanks([]);
      setAlienRanks([]);
    }
  }, [ranksLoaded]);

  // Функция определения лучшего ранга игрока
  const getBestPlayerRank = useCallback((player: Player) => {
    if (!ranksLoaded || !humanRanks.length || !alienRanks.length) {
      return null;
    }

    const stats = getPlayerStats(player);
    const humanPoints = stats.humanRank;
    const alienPoints = stats.alienRank;
    
    // Определяем лучший ранг (больше очков, при равенстве - human)
    const isHumanBetter = humanPoints >= alienPoints;
    const bestPoints = isHumanBetter ? humanPoints : alienPoints;
    const ranksToSearch = isHumanBetter ? humanRanks : alienRanks;
    
    // Если нет очков, возвращаем первый ранг
    if (bestPoints === 0) {
      return ranksToSearch[0] || null;
    }
    
    // Ищем подходящий ранг (наивысший ранг, который игрок может достичь)
    let bestRank = ranksToSearch[0];
    for (const rank of ranksToSearch) {
      if (bestPoints >= rank.pointsRequired) {
        bestRank = rank;
      } else {
        break;
      }
    }
    
    return bestRank;
  }, [ranksLoaded, humanRanks, alienRanks, getPlayerStats]);



  // Основной useEffect для загрузки данных при смене типа игры
  useEffect(() => {
    // Предотвращаем дублирование вызовов
    if (loadingRef.current || currentGameTypeRef.current === selectedGameType) {
      return;
    }
    
    const loadData = async () => {
      loadingRef.current = true;
      currentGameTypeRef.current = selectedGameType;
      setLoading(true);
      setError(null);
      setHasMorePlayers(false);
      setAllPlayers([]);
      setIsDataLoaded(false);
      
      try {
        // Загружаем данные параллельно (включая ранги)
        const [topPlayersResponse, allPlayersResponse] = await Promise.all([
          userService.getTopPlayers(selectedGameType, 10),
          userService.getPlayers(selectedGameType, 1, 50),
          loadRanks()
        ]);
        
        setTopPlayers(topPlayersResponse || []);
        setAllPlayers(allPlayersResponse.players || []);
        setHasMorePlayers(allPlayersResponse.hasMore || false);
        setIsDataLoaded(true);
      } catch (err: any) {
        setError('Failed to load player data');
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    };
    
    loadData();
  }, [selectedGameType]);

  // Функция для повторного вызова данных
  const retryLoadData = useCallback(async () => {
    if (loadingRef.current) return;
    
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    setHasMorePlayers(false);
    setAllPlayers([]);
    setIsDataLoaded(false);
    
    try {
      const [topPlayersResponse, allPlayersResponse] = await Promise.all([
        userService.getTopPlayers(selectedGameType, 10),
        userService.getPlayers(selectedGameType, 1, 50)
      ]);
      
      setTopPlayers(topPlayersResponse || []);
      setAllPlayers(allPlayersResponse.players || []);
      setHasMorePlayers(allPlayersResponse.hasMore || false);
      setIsDataLoaded(true);
    } catch (err: any) {
      setError('Failed to load player data');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [selectedGameType]);

  const handleSearch = useCallback(async (term: string) => {
    if (!term.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      const results = await userService.searchPlayers(term, 20, selectedGameType);
      setSearchResults(results || []);
    } catch (err) {
      setError('Failed to search players');
    } finally {
      setSearching(false);
    }
  }, [selectedGameType]);

  const handleSearchInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Очищаем предыдущий таймер
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Дебаунс поиска
    if (value.length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        handleSearch(value);
      }, 300);
    } else {
      setSearchResults([]);
    }
  }, [handleSearch]);

  const handlePlayerClick = useCallback((playerId: string) => {
    navigate(`/profile/${playerId}`);
  }, [navigate]);

  const handleCreateAccount = useCallback(() => {
    navigate('/register');
  }, [navigate]);

  const handleMyProfile = useCallback(() => {
    navigate('/profile');
  }, [navigate]);

  // Очистка таймера и ref при размонтировании
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      loadingRef.current = false;
      currentGameTypeRef.current = '';
    };
  }, []);

  if (loading) {
    return (
      <PageBackground $shadow={isShadowGrid}>
        {isNightOpsMode && <DottedOverlay />}
        {isModernWarfare && <ModernBackground />}
        <Container>
          <LoadingSpinner>Loading commandos data...</LoadingSpinner>
        </Container>
      </PageBackground>
    );
  }

  if (error) {
    return (
      <PageBackground $shadow={isShadowGrid}>
        {isNightOpsMode && <DottedOverlay />}
        {isModernWarfare && <ModernBackground />}
        <Container>
          <ErrorMessage>
            {error}
            <RetryButton onClick={retryLoadData}>Retry</RetryButton>
          </ErrorMessage>
        </Container>
      </PageBackground>
    );
  }

  return (
    <PageBackground $shadow={isShadowGrid}>
      {isNightOpsMode && <DottedOverlay />}
      {isModernWarfare && <ModernBackground />}
      <Container>
        <Header>
          <Title>{getPageTitle()}</Title>
          <Subtitle>{getPageSubtitle()}</Subtitle>
        </Header>

        {/* Game Type Selector */}
        <GameTypeSelector>
          <GameTypeButton 
            $active={selectedGameType === 'airsoft'}
            onClick={() => handleGameTypeChange('airsoft')}
          >
            [Airsoft]
          </GameTypeButton>
          <GameTypeButton 
            $active={selectedGameType === 'paintball'}
            onClick={() => handleGameTypeChange('paintball')}
          >
            [Paintball]
          </GameTypeButton>
        </GameTypeSelector>

      <SearchSection>
        <SearchInput
          type="text"
          placeholder="Search commandos by name or tag..."
          value={searchTerm}
          onChange={handleSearchInputChange}
        />
        {user ? (
          <ActionButton onClick={handleMyProfile}>
            My Profile
          </ActionButton>
        ) : !isFieldAuthenticated ? (
          <ActionButton onClick={handleCreateAccount}>
            Create Account
          </ActionButton>
        ) : null}
      </SearchSection>

      {searching && <SearchingIndicator>Searching...</SearchingIndicator>}

      {/* Результаты поиска */}
      {searchResults.length > 0 && (
        <SearchResultsSection>
          <SearchResultsTitle>Search Results</SearchResultsTitle>
          <SearchResultsList>
            {searchResults.map((player) => {
              const stats = getPlayerStats(player);
              return (
                <SearchResultsCard
                  key={player.id}
                  onClick={() => handlePlayerClick(player.id)}
                >
                  <SearchResultsInfo>
                    <SearchResultsName>{player.name}</SearchResultsName>
                    <SearchResultsTag>{player.primaryBase}</SearchResultsTag>
                  </SearchResultsInfo>
                  <SearchResultsFooter>
                    <SearchResultsStats>
                      <SearchResultsStat>
                        <SearchResultsStatLabel>Games:</SearchResultsStatLabel>
                        <SearchResultsStatValue>{stats.missions}</SearchResultsStatValue>
                      </SearchResultsStat>
                      <SearchResultsStat>
                        <SearchResultsStatLabel>Win Rate:</SearchResultsStatLabel>
                        <SearchResultsStatValue>{stats.winRate.toFixed(1)}%</SearchResultsStatValue>
                      </SearchResultsStat>
                    </SearchResultsStats>
                    <SearchResultsRankBadge>
                      Rank {stats.totalRank}
                    </SearchResultsRankBadge>
                  </SearchResultsFooter>
                </SearchResultsCard>
              );
            })}
          </SearchResultsList>
        </SearchResultsSection>
      )}

      {/* Топ 10 коммандос */}
      <TopPlayersSection>
        <SectionTitle>Top 10 Commandos</SectionTitle>
        <TopPlayersList>
          {topPlayers.map((player, index) => {
            const stats = getPlayerStats(player);
            return (
              <TopPlayerItem
                key={player.id}
                onClick={() => handlePlayerClick(player.id)}
              >
                <TopPlayerRank>#{index + 1}</TopPlayerRank>
                <TopPlayerInfo>
                  <TopPlayerHeader>
                    <TopPlayerNameColumn>
                      <TopPlayerName>{player.name}</TopPlayerName>
                      <TopPlayerRankTitle $faction={stats.isHumanBetter ? 'human' : 'alien'}>
                        {stats.isHumanBetter ? stats.humanRankName : stats.alienRankName}
                      </TopPlayerRankTitle>
                    </TopPlayerNameColumn>
                    <CompactPlayerRank player={player} getBestRank={getBestPlayerRank} getPlayerStats={getPlayerStats} />
                  </TopPlayerHeader>
                </TopPlayerInfo>
                <TopPlayerStats>
                  <TopPlayerStat>
                    <TopPlayerStatLabel>Games:</TopPlayerStatLabel>
                    <TopPlayerStatValue>{stats.missions}</TopPlayerStatValue>
                  </TopPlayerStat>
                  <TopPlayerStat>
                    <TopPlayerStatLabel>Win Rate:</TopPlayerStatLabel>
                    <TopPlayerStatValue>{stats.winRate.toFixed(1)}%</TopPlayerStatValue>
                  </TopPlayerStat>
                  <TopPlayerStat>
                    <TopPlayerStatLabel>Points:</TopPlayerStatLabel>
                    <TopPlayerStatValue $isPoints={true} $isHumanBetter={stats.isHumanBetter}>{stats.totalPoints}</TopPlayerStatValue>
                  </TopPlayerStat>
                </TopPlayerStats>
                <TopPlayerFooter>
                  <TopPlayerRankBadge>
                    Rank {stats.totalRank}
                  </TopPlayerRankBadge>
                </TopPlayerFooter>
              </TopPlayerItem>
            );
          })}
        </TopPlayersList>
      </TopPlayersSection>

      {/* Все коммандос */}
      <AllPlayersSection>
        <SectionTitle>All Commandos</SectionTitle>
        
        <CommandosStats>
          <CommandosStat>
            <CommandosStatLabel>Total Commandos:</CommandosStatLabel>
            <CommandosStatValue>{allPlayers.length}</CommandosStatValue>
          </CommandosStat>
          <CommandosStat>
            <CommandosStatLabel>Active Players:</CommandosStatLabel>
            <CommandosStatValue>{allPlayers.filter(p => (getPlayerStats(p).missions || 0) > 0).length}</CommandosStatValue>
          </CommandosStat>
          <CommandosStat>
            <CommandosStatLabel>Total Games:</CommandosStatLabel>
            <CommandosStatValue>{allPlayers.reduce((sum, p) => sum + (getPlayerStats(p).missions || 0), 0)}</CommandosStatValue>
          </CommandosStat>
        </CommandosStats>

        <PlayersList>
          {allPlayers.map((player, index) => {
            const stats = getPlayerStats(player);
            return (
              <PlayerListItem
                key={player.id}
                onClick={() => handlePlayerClick(player.id)}
              >
                <PlayerRankPosition>#{index + 1}</PlayerRankPosition>
                <PlayerMainInfo>
                  <PlayerListHeader>
                    <PlayerListName>{player.name}</PlayerListName>
                  </PlayerListHeader>
                  <PlayerListLocation>
                    <PlayerListBase>{player.primaryBase}</PlayerListBase>
                    <span> • {player.state}</span>
                    {player.secondaryBases.length > 0 && (
                      <span> • {player.secondaryBases.length} secondary bases</span>
                    )}
                  </PlayerListLocation>
                </PlayerMainInfo>
                <PlayerStatsGrid>
                  <PlayerStatItem>
                    <PlayerStatItemLabel>Games</PlayerStatItemLabel>
                    <PlayerStatItemValue>{stats.missions}</PlayerStatItemValue>
                  </PlayerStatItem>
                  <PlayerStatItem>
                    <PlayerStatItemLabel>Win Rate</PlayerStatItemLabel>
                    <PlayerStatItemValue>{stats.winRate.toFixed(1)}%</PlayerStatItemValue>
                  </PlayerStatItem>
                  <PlayerStatItem>
                    <PlayerStatItemLabel>Points</PlayerStatItemLabel>
                    <PlayerStatItemValue $isPoints={true} $isHumanBetter={stats.isHumanBetter}>{stats.totalPoints}</PlayerStatItemValue>
                  </PlayerStatItem>
                </PlayerStatsGrid>
                <PlayerRank player={player} getBestRank={getBestPlayerRank} getPlayerStats={getPlayerStats} />
                <PlayerRanksInfo>
                  <PlayerRankInfo>
                    <PlayerRankLabel>Human:</PlayerRankLabel>
                    <PlayerRankName $faction="human">{stats.humanRankName}</PlayerRankName>
                  </PlayerRankInfo>
                  <PlayerRankInfo>
                    <PlayerRankLabel>Alien:</PlayerRankLabel>
                    <PlayerRankName $faction="alien">{stats.alienRankName}</PlayerRankName>
                  </PlayerRankInfo>
                </PlayerRanksInfo>
              </PlayerListItem>
            );
          })}
        </PlayersList>
      </AllPlayersSection>
      </Container>
    </PageBackground>
  );
};

export default CommandosPage;
